/**
 * Date-realism forecast (P1-17/P1-18, AC-6/AC-7) -- pure function of
 * (event log, objectiveId, today) computing `ObjectiveForecastView`
 * (domain-api.ts). Core product math: held to the ~100% coverage bar
 * (PRD §9 / CLAUDE.md quality bar), unlike the rest of the domain layer.
 *
 * Algorithm (documented here per the contract's instruction that the
 * trailing window is "documented in its implementation, not fixed" in the
 * type):
 *
 * 1. Pace = actions completed per week, averaged over the window from the
 *    objective's `objective.created` date (`occurredOn`) through `today`,
 *    inclusive, using elapsed calendar days / 7. While that window is empty
 *    (created today, or zero completions yet) there is by definition "not
 *    enough data" and the forecast degrades to the neutral all-null/false
 *    shape mandated by the contract ("never a false or divide-by-zero
 *    forecast", AC-6).
 * 2. Only ACTIVE completions count (a reverted `action.completed` is
 *    treated as absent, matching `log_entry.reverted` semantics everywhere
 *    else, ADR-0002). Duplicate completions for the same actionId collapse
 *    to one (mirrors habit-completion "duplicates collapse", ADR-0002).
 * 3. `actionsRemaining` = total non-removed actions - distinct completed
 *    actions, counted as of `today`.
 * 4. `forecastFinishDate` = today + ceil(actionsRemaining / pace) weeks,
 *    converted to a calendar date. Zero actions remaining -> the objective is
 *    already done; no forecast is needed and the view reports it as such
 *    (never slipping).
 * 5. `isSlipping` = forecastFinishDate is strictly after targetDate. Never
 *    true when there is no forecast yet (AC-6: "never claims a slip without
 *    data").
 * 6. When slipping, `suggestion.suggestedTargetDate` = forecastFinishDate
 *    (the one-click "push the date out to what pace actually supports"), and
 *    `suggestion.requiredPaceActionsPerWeek` = the pace needed to hit the
 *    EXISTING targetDate: actionsRemaining / weeks-until-target (weeks
 *    computed from today, minimum 1 day to avoid divide-by-zero when the
 *    target date is today or in the past).
 *
 * AC-6's "at least 2 weeks of action-completion history" describes the
 * common case where a meaningful pace exists; this implementation does not
 * hard-code "2 weeks" as a gate -- it gates on "at least one completed
 * action inside a non-zero window", the more general statement of the same
 * "don't show false precision" requirement, and degrades identically for a
 * brand-new objective (zero history -> not-enough-data, exactly AC-6's "no
 * completion history" case).
 *
 * Phase 5 additions (ADR-0002 addendum 2026-07-04 D5/D6/D8/D9/D10):
 * confidence %, buffer weeks, the cadence-goal comparison, and the
 * draft/paused/computed status tri-state are all layered on top of the SAME
 * pace/date math above with no change to it (AC-O16). The pure math core is
 * factored into `computeForecastFromInput`, parameterized over
 * `ObjectiveForecastInput`, so the exact same function can run over either
 * committed event-log state (`computeObjectiveForecast`, unchanged entry
 * point) or an in-progress, unsaved draft's field values
 * (`computeDraftForecastPreview`, new) -- one implementation, never forked.
 */

import type {
  CompassEvent,
  EventId,
  IsoDate,
  ObjectiveForecastView,
  ObjectiveId,
} from '../../contracts';
import { eventsOfTypes, excludingReverted } from '../event-log/event-log';
import { addDaysToDate, weekOf } from '../habits/week';
import { buildObjectiveStates } from '../objectives/state';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DAYS_PER_WEEK = 7;
/**
 * Confidence's history ramp reaches full confidence at 4 weeks (ADR-0002
 * addendum 2026-07-04 D5) -- reuses the weekly-review trend window constant
 * (`review/weekly-review.ts`'s `TREND_WEEKS`) as an already-accepted
 * "meaningful window" rather than inventing a new one.
 */
const CONFIDENCE_FULL_HISTORY_WEEKS = 4;

/**
 * The neutral "not enough data yet" shape (AC-6: never a false or
 * divide-by-zero forecast). `appliedAdjustmentEventId` (domain-api.ts) is a
 * property of the objective's CURRENT target date, not of whether a
 * forecast could be computed -- so every "not enough data"/"already done"
 * return still carries it through from `state`; only the unknown-objective
 * early return (no `state` to read) passes null explicitly. Factored into
 * one function (rather than repeating the object literal at each of the
 * guard clauses that degrade to this shape) so the guard clauses stay one
 * line each. Phase 5: also carries the neutral `confidence`/`bufferWeeks`/
 * `status`/cadence-goal fields (ADR-0002 addendum 2026-07-04 D5/D6/D8).
 */
function notEnoughData(
  actionsRemaining: number,
  appliedAdjustmentEventId: EventId | null,
  cadenceGoalPerWeek: number | null = null,
): ObjectiveForecastView {
  return {
    paceActionsPerWeek: null,
    actionsRemaining,
    forecastFinishDate: null,
    isSlipping: false,
    suggestion: null,
    appliedAdjustmentEventId,
    status: 'not_enough_data',
    confidence: 0,
    bufferWeeks: null,
    cadenceGoalPerWeek,
    actualVsCadenceGoal: null,
  };
}

export function computeObjectiveForecast(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
  today: IsoDate | string,
): ObjectiveForecastView {
  const states = buildObjectiveStates(events);
  const state = states.get(objectiveId);

  if (!state) {
    // Unknown objective: contract has no explicit "not found" shape for a
    // forecast view (callers are expected to check getObjective's null
    // first) -- degrade to the neutral no-data shape rather than throw, so
    // this pure function never crashes on an unexpected objectiveId. No
    // state to read appliedAdjustmentEventId from -- null.
    return notEnoughData(0, null);
  }

  // Phase 5 (ADR-0002 addendum 2026-07-04 D8): a draft objective has no
  // persisted forecast at all -- the neutral not-enough-data shape IS the
  // contract's documented behavior for a draft ("not computed on the
  // persisted objective"). The live preview sidebar computes a SEPARATE,
  // in-memory-only number via `computeDraftForecastPreview` below; nothing
  // here changes for that flow.
  if (state.status === 'draft') {
    return notEnoughData(activeActionCount(state), null, state.cadenceGoalPerWeek);
  }

  const activeActionIds = new Set(
    [...state.actionsById.values()]
      .filter((action) => !action.removed)
      .map((action) => action.actionId),
  );
  const totalActions = activeActionIds.size;

  const completionEvents = excludingReverted(
    eventsOfTypes(events, ['action.completed', 'log_entry.reverted']),
  ).filter(
    (event): event is Extract<CompassEvent, { type: 'action.completed' }> =>
      event.type === 'action.completed' &&
      event.objectiveId === objectiveId &&
      activeActionIds.has(event.actionId),
  );

  // Duplicates for the same actionId should not double-count "actions
  // completed" -- collapse to one completion per actionId.
  const completedActionIds = new Set(completionEvents.map((e) => e.actionId));

  const input: ObjectiveForecastInput = {
    targetDate: state.targetDate as IsoDate,
    createdOn: state.createdOn as IsoDate,
    totalActions,
    completedActionCount: completedActionIds.size,
    weeklyCompletionCounts: weeklyCompletionCounts(
      completionEvents.map((e) => e.occurredOn),
      state.createdOn,
      today,
    ),
    cadenceGoalPerWeek: state.cadenceGoalPerWeek,
    appliedAdjustmentEventId: state.appliedAdjustmentEventId,
  };

  const computed = computeForecastFromInput(input, today);

  // Phase 5 (ADR-0002 addendum 2026-07-04 D9): on_hold freezes to a neutral
  // "Paused" verdict -- never a slip/pace verdict, even when the underlying
  // math would otherwise indicate slipping. The real pace/date/confidence/
  // buffer numbers still pass through (never fabricate an absence of data);
  // only isSlipping/suggestion are forced neutral.
  if (state.status === 'on_hold') {
    return {
      ...computed,
      status: 'paused',
      isSlipping: false,
      suggestion: null,
    };
  }

  return computed;
}

function activeActionCount(state: { readonly actionsById: ReadonlyMap<unknown, { readonly removed: boolean }> }): number {
  let count = 0;
  for (const action of state.actionsById.values()) {
    if (!action.removed) count += 1;
  }
  return count;
}

// ---------------------------------------------------------------------------
// Phase 5 (ADR-0002 addendum 2026-07-04 D10): the shared pure math core,
// parameterized over either committed event-log state or a hypothetical
// draft's field values.
// ---------------------------------------------------------------------------

export interface ObjectiveForecastInput {
  readonly targetDate: IsoDate;
  readonly createdOn: IsoDate;
  readonly totalActions: number;
  readonly completedActionCount: number;
  /**
   * Completions per CLOSED week since `createdOn`, oldest first -- feeds
   * confidence's variance factor (D5). Empty array for a draft preview
   * (nothing is ever completed in an unsaved draft) or for an objective with
   * fewer than one closed week of history.
   */
  readonly weeklyCompletionCounts: readonly number[];
  readonly cadenceGoalPerWeek: number | null;
  readonly appliedAdjustmentEventId: EventId | null;
}

/**
 * The shared pace/date/confidence/buffer/cadence math (ADR-0002 addendum
 * 2026-07-04 D5/D6/D10) -- runs identically whether `input` was assembled
 * from real committed event-log state (`computeObjectiveForecast`) or an
 * in-progress, unsaved draft (`computeDraftForecastPreview`). This is the
 * ONLY place this math is implemented; neither caller duplicates it.
 */
export function computeForecastFromInput(
  input: ObjectiveForecastInput,
  today: IsoDate | string,
): ObjectiveForecastView {
  const actionsRemaining = Math.max(0, input.totalActions - input.completedActionCount);

  if (actionsRemaining === 0) {
    // Objective fully complete (or empty plan): no forecast needed, never
    // slipping. Cadence-goal comparison has nothing to compare against
    // either (no actual pace once there's nothing remaining to forecast).
    return {
      paceActionsPerWeek: null,
      actionsRemaining: 0,
      forecastFinishDate: null,
      isSlipping: false,
      suggestion: null,
      appliedAdjustmentEventId: input.appliedAdjustmentEventId,
      status: 'not_enough_data',
      confidence: 0,
      bufferWeeks: null,
      cadenceGoalPerWeek: input.cadenceGoalPerWeek,
      actualVsCadenceGoal: null,
    };
  }

  const windowDays = daysBetween(input.createdOn, today);

  if (windowDays === null || windowDays <= 0 || input.completedActionCount === 0) {
    return notEnoughData(actionsRemaining, input.appliedAdjustmentEventId, input.cadenceGoalPerWeek);
  }

  const pace = (input.completedActionCount / windowDays) * DAYS_PER_WEEK;

  // Defense in depth: with >=1 completion and a positive window, `pace` is
  // mathematically always > 0 today, but a pace that is zero, negative, or
  // non-finite (e.g. a pathological/extreme window) must never be allowed to
  // reach the division below -- that is exactly the "no pace established"
  // condition QA flagged, and it degrades to the same neutral not-enough-data
  // shape as a brand-new objective rather than ever producing NaN, Infinity,
  // or an invalid date.
  if (!Number.isFinite(pace) || pace <= 0) {
    return notEnoughData(actionsRemaining, input.appliedAdjustmentEventId, input.cadenceGoalPerWeek);
  }

  const weeksNeeded = actionsRemaining / pace;
  const daysNeeded = Math.ceil(weeksNeeded * DAYS_PER_WEEK);

  if (!Number.isFinite(daysNeeded)) {
    return notEnoughData(actionsRemaining, input.appliedAdjustmentEventId, input.cadenceGoalPerWeek);
  }

  const forecastFinishDate = addDays(today, daysNeeded);

  const isSlipping = forecastFinishDate > input.targetDate;

  const suggestion = isSlipping
    ? {
        suggestedTargetDate: forecastFinishDate as IsoDate,
        requiredPaceActionsPerWeek: requiredPace(
          actionsRemaining,
          today,
          input.targetDate,
        ),
      }
    : null;

  const weeksOfHistory = windowDays / DAYS_PER_WEEK;
  const confidence = computeConfidence(weeksOfHistory, input.weeklyCompletionCounts);
  const bufferWeeks = Math.round(
    (daysBetween(forecastFinishDate, input.targetDate) ?? 0) / DAYS_PER_WEEK,
  );
  const actualVsCadenceGoal = computeActualVsCadenceGoal(pace, input.cadenceGoalPerWeek);

  return {
    paceActionsPerWeek: pace,
    actionsRemaining,
    forecastFinishDate: forecastFinishDate as IsoDate,
    isSlipping,
    suggestion,
    appliedAdjustmentEventId: input.appliedAdjustmentEventId,
    status: 'computed',
    confidence,
    bufferWeeks,
    cadenceGoalPerWeek: input.cadenceGoalPerWeek,
    actualVsCadenceGoal,
  };
}

/**
 * The live, unsaved-draft forecast preview (R3.9/spec §0 item 10, ADR-0002
 * addendum 2026-07-04 D10) -- computes a forecast from in-progress form
 * field values WITHOUT any event-log involvement and WITHOUT persisting
 * anything. Delegates to `computeForecastFromInput` (the same core
 * `computeObjectiveForecast` uses) so the preview cannot drift from the
 * committed math. Pure, no I/O -- callable directly from `src/ui` on every
 * keystroke (ADR-0001 rule 4).
 *
 * `createdOn` is set to `today` (a draft has no creation date yet -- "as if
 * created today" is the only coherent hypothetical), `completedActionCount`
 * is always 0 (draft actions are never checkable), and
 * `weeklyCompletionCounts` is always empty -- so this naturally resolves
 * through the same not-enough-data branch every brand-new committed
 * objective would also hit. `actionsRemaining` (= `draft.actionCount`) is
 * still meaningful even pre-pace.
 */
export function computeDraftForecastPreview(
  draft: {
    readonly targetDate: IsoDate;
    readonly cadenceGoalPerWeek: number | null;
    readonly actionCount: number;
  },
  today: IsoDate | string,
): ObjectiveForecastView {
  const input: ObjectiveForecastInput = {
    targetDate: draft.targetDate,
    createdOn: today as IsoDate,
    totalActions: draft.actionCount,
    completedActionCount: 0,
    weeklyCompletionCounts: [],
    cadenceGoalPerWeek: draft.cadenceGoalPerWeek,
    appliedAdjustmentEventId: null,
  };
  return computeForecastFromInput(input, today);
}

// ---------------------------------------------------------------------------
// Confidence and buffer (ADR-0002 addendum 2026-07-04 D5)
// ---------------------------------------------------------------------------

/**
 * `confidence = round(historyFactor(weeksOfHistory) * varianceFactor(counts) * 100)`,
 * clamped to [0, 100]. See ADR-0002 addendum 2026-07-04 D5 for the full
 * derivation and worked examples (0/4/0/4 vs. steady 2/2/2/2).
 */
function computeConfidence(weeksOfHistory: number, weeklyCompletionCounts: readonly number[]): number {
  const historyFactor = Math.min(1, Math.max(0, weeksOfHistory) / CONFIDENCE_FULL_HISTORY_WEEKS);
  const variance = computeVarianceFactor(weeklyCompletionCounts);
  const raw = Math.round(historyFactor * variance * 100);
  return Math.min(100, Math.max(0, raw));
}

/**
 * `1 / (1 + cv)` where `cv` is the coefficient of variation (population
 * stddev / mean) of `counts`. Fewer than 2 data points -> exactly 1 (no
 * variance penalty beyond what `historyFactor` already applies -- a single
 * data point has no variance to measure). `mean === 0` -> `cv = 0` -> factor
 * `1` is WRONG for the "some weeks 0, some weeks not" case, so mean-zero is
 * special-cased to use the raw stddev against a variance-only view instead
 * of dividing by a zero mean; in practice this only fires when every
 * counted week is truly zero (which cannot happen here, since callers only
 * reach this function once at least one completion exists across the
 * window), so it is defense-in-depth, not a reachable product case.
 */
function computeVarianceFactor(counts: readonly number[]): number {
  if (counts.length < 2) return 1;
  const mean = counts.reduce((sum, n) => sum + n, 0) / counts.length;
  if (mean === 0) return 1;
  const variance = counts.reduce((sum, n) => sum + (n - mean) ** 2, 0) / counts.length;
  const stdDev = Math.sqrt(variance);
  const cv = stdDev / mean;
  return 1 / (1 + cv);
}

/**
 * Completions-per-CLOSED-week since `createdOn`, oldest first, up through
 * (but not including) the week containing `today` -- feeds
 * `computeConfidence`'s variance factor. A week with zero completions inside
 * the window still contributes an explicit `0` entry (not omitted), so an
 * erratic 0/4/0/4 pattern is visible to the variance calculation exactly as
 * ADR-0002 addendum 2026-07-04 D5 requires.
 */
function weeklyCompletionCounts(
  completionDates: readonly string[],
  createdOn: string,
  today: string,
): readonly number[] {
  const createdWeek = weekOf(createdOn);
  const currentWeek = weekOf(today);
  if (createdWeek >= currentWeek) return [];

  const counts = new Map<string, number>();
  for (const date of completionDates) {
    const week = weekOf(date);
    counts.set(week, (counts.get(week) ?? 0) + 1);
  }

  const result: number[] = [];
  let cursor = createdWeek;
  while (cursor < currentWeek) {
    result.push(counts.get(cursor) ?? 0);
    cursor = weekOf(addDaysToDate(cursor, DAYS_PER_WEEK));
  }
  return result;
}

/**
 * The second, additional comparison R3.12 introduces (ADR-0002 addendum
 * 2026-07-04 D6): actual pace vs. the user's stated cadence goal. Null when
 * no goal is set. Does not read or affect `paceActionsPerWeek`/
 * `forecastFinishDate`/`isSlipping` in any way -- purely a second read of
 * numbers already computed.
 */
function computeActualVsCadenceGoal(
  actualPerWeek: number,
  cadenceGoalPerWeek: number | null,
): ObjectiveForecastView['actualVsCadenceGoal'] {
  if (cadenceGoalPerWeek === null) return null;
  return {
    actualPerWeek,
    goalPerWeek: cadenceGoalPerWeek,
    aheadOfGoal: actualPerWeek >= cadenceGoalPerWeek,
  };
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

/** Whole days between two IsoDate strings (b - a); null if either is invalid. */
function daysBetween(a: string | null, b: string): number | null {
  if (a === null) return null;
  const aMs = parseIsoDate(a);
  const bMs = parseIsoDate(b);
  if (aMs === null || bMs === null) return null;
  return Math.round((bMs - aMs) / MS_PER_DAY);
}

function parseIsoDate(date: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return null;
  const [, y, m, d] = match;
  return Date.UTC(Number(y), Number(m) - 1, Number(d));
}

function addDays(date: string, days: number): string {
  const ms = parseIsoDate(date);
  if (ms === null) return date;
  const result = new Date(ms + days * MS_PER_DAY);
  const year = result.getUTCFullYear().toString().padStart(4, '0');
  const month = (result.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = result.getUTCDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Actions/week required to hit `targetDate` from `today`, given
 * `actionsRemaining`. Minimum window of 1 day when the target date is today
 * or already in the past, so this never divides by zero (AC-6).
 */
function requiredPace(
  actionsRemaining: number,
  today: string,
  targetDate: string,
): number {
  const rawDays = daysBetween(today, targetDate) ?? 0;
  const days = Math.max(1, rawDays);
  return (actionsRemaining / days) * DAYS_PER_WEEK;
}
